import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

const DEFAULT_FORM = {
  name: "",
  email: "",
  password: "",
  isActive: true,
  roles: ["USR"]
};

export default function UserFormModal({ show, onClose, onSaved, user, roles }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEditing = Boolean(user);

  useEffect(() => {
    if (!show) return;

    setForm(user ? {
      name: user.name ?? "",
      email: user.email ?? "",
      password: "",
      isActive: user.isActive !== false,
      roles: (user.roles ?? []).map((role) => role.code)
    } : DEFAULT_FORM);
    setError("");
    setSaving(false);
  }, [show, user]);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value
    }));
  };

  const toggleRole = (roleCode) => {
    setForm((current) => {
      const roleSet = new Set(current.roles);

      if (roleSet.has(roleCode)) {
        roleSet.delete(roleCode);
      } else {
        roleSet.add(roleCode);
      }

      return {
        ...current,
        roles: Array.from(roleSet)
      };
    });
  };

  const saveUser = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      isActive: form.isActive,
      roles: form.roles
    };

    if (form.password && (isEditing || !payload.email)) {
      payload.password = form.password;
    }

    if (!isEditing && !payload.email && !payload.password) {
      setSaving(false);
      setError("Password is required when the user has no email.");
      return;
    }

    try {
      const res = await fetchWithAuth(isEditing ? `/api/users/${user.id}` : "/api/users", {
        method: isEditing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const responseError = await res.json().catch(() => null);
        throw new Error(responseError?.error || "Save failed");
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Form onSubmit={saveUser}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Edit user" : "Add user"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <div className="alert alert-danger py-2">{error}</div>
          )}

          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={form.name}
                  onChange={(event) => updateField("name", event.target.value)}
                  required
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={(event) => updateField("email", event.target.value)}
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>{isEditing ? "New password" : "Password"}</Form.Label>
                <Form.Control
                  type="password"
                  value={form.password}
                  onChange={(event) => updateField("password", event.target.value)}
                  placeholder={
                    isEditing
                      ? "Leave empty to keep current password"
                      : form.email
                        ? "Generated and sent by email"
                        : ""
                  }
                  disabled={!isEditing && Boolean(form.email)}
                  required={!isEditing && !form.email}
                />
                {!isEditing && form.email && (
                  <Form.Text muted>
                    A temporary password will be generated and sent to this email.
                  </Form.Text>
                )}
              </Form.Group>
            </div>

            <div className="col-md-6 d-flex align-items-end">
              <Form.Check
                type="switch"
                id="user-active-switch"
                label="Active user"
                checked={form.isActive}
                onChange={(event) => updateField("isActive", event.target.checked)}
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="fw-semibold mb-2">Roles</div>
            <div className="d-flex flex-wrap gap-3">
              {roles.map((role) => (
                <Form.Check
                  key={role.code}
                  type="checkbox"
                  id={`role-${role.code}`}
                  label={`${role.code} - ${role.name}`}
                  checked={form.roles.includes(role.code)}
                  onChange={() => toggleRole(role.code)}
                />
              ))}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
